// Formatting + severity helpers shared by the dynamic-analysis result UI.
// Lifted from the (previously duplicated) logic in ResultDetailPage /
// JobDetailPage so both pages and the new components stay in sync.

import type {
  GhostTransactionAnalysis,
  MetaAnalysisLite,
  SignificanceTest,
  ImpactAlert,
} from './types';

// ---------------------------------------------------------------------------
// Durations & dates
// ---------------------------------------------------------------------------

/** Coarse, minute-grain duration — for window lengths and cycle estimates. */
export const formatDuration = (seconds: number): string => {
  if (!Number.isFinite(seconds)) return 'N/A';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  if (seconds < 2592000) return `${(seconds / 86400).toFixed(1)}d`;
  return `${(seconds / 2592000).toFixed(1)}mo`;
};

/** Finer-grain gap (inter-arrival) — keeps seconds while small so a 62s vs
 *  175s difference stays legible instead of both rounding to "3m". */
export const formatGap = (seconds: number): string => {
  if (!Number.isFinite(seconds)) return 'N/A';
  const s = Math.max(0, Math.round(seconds));
  if (s < 90) return `${s}s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
  }
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

export const formatShortDate = (isoString: string): string => {
  try {
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'N/A';
  }
};

/** Compact "May 26" used for historical-bucket week labels. */
export const formatMonthDay = (isoString: string): string => formatShortDate(isoString);

// ---------------------------------------------------------------------------
// Numbers / money
// ---------------------------------------------------------------------------

export const formatNumber = (n: number, maxFractionDigits = 2): string =>
  n.toLocaleString('en-US', { maximumFractionDigits: maxFractionDigits });

export const formatSigned = (n: number, maxFractionDigits = 2): string =>
  `${n > 0 ? '+' : ''}${formatNumber(n, maxFractionDigits)}`;

/** Currency-ish value with a leading $ (ghost median / missing value). */
export const formatMoney = (n: number): string => `$${formatNumber(n)}`;

/** A field/impact value honoring its unit: a 3-letter code → currency-ish,
 *  an arbitrary unit → suffixed, null/absent → bare number (per the contract,
 *  fields may carry no unit). */
export const formatWithUnit = (n: number, unit?: string | null): string => {
  if (!unit) return formatNumber(n);
  if (/^[A-Z]{3}$/.test(unit)) return `${formatNumber(n)} ${unit}`;
  return `${formatNumber(n)} ${unit}`;
};

/** Signed impact estimate with its unit; null → em dash. */
export const formatImpact = (alert: ImpactAlert | undefined): string => {
  const v = alert?.estimatedImpactCountNormalized;
  if (v == null) return '—';
  return `${v > 0 ? '+' : ''}${formatWithUnit(v, alert?.unitOfMeasure)}`;
};

/** "order_total" → "Order Total". */
export const titleCaseSlug = (slug: string): string =>
  slug
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (m) => m.toUpperCase());

// ---------------------------------------------------------------------------
// Severity / significance
// ---------------------------------------------------------------------------

export interface SeverityStyle {
  label: string;
  text: string;
  bg: string;
  dot: string;
  border: string;
  /** Closest shared Badge variant. */
  badge: 'success' | 'warning' | 'error' | 'neutral';
}

const NO_READ: SeverityStyle = {
  label: 'No read',
  text: 'text-muted-foreground',
  bg: 'bg-muted',
  dot: 'bg-muted-foreground',
  border: 'border-border',
  badge: 'neutral',
};

const SEVERITY_STYLES: Record<SignificanceTest, SeverityStyle> = {
  Normal: {
    label: 'Normal',
    text: 'text-success',
    bg: 'bg-success/15',
    dot: 'bg-success',
    border: 'border-success/20',
    badge: 'success',
  },
  Caution: {
    label: 'Caution',
    text: 'text-warn',
    bg: 'bg-warn/15',
    dot: 'bg-warn',
    border: 'border-warn/20',
    badge: 'warning',
  },
  Dangerous: {
    label: 'Dangerous',
    text: 'text-alert',
    bg: 'bg-alert/15',
    dot: 'bg-alert',
    border: 'border-alert/20',
    badge: 'error',
  },
  'ALERT!': {
    label: 'ALERT!',
    text: 'text-destructive',
    bg: 'bg-destructive/15',
    dot: 'bg-destructive',
    border: 'border-destructive/20',
    badge: 'error',
  },
};

/** Styling for a severity level; a null/undefined level → muted "no read". */
export const severityStyle = (test: SignificanceTest | null | undefined): SeverityStyle =>
  test ? SEVERITY_STYLES[test] ?? NO_READ : NO_READ;

export const SEVERITY_RANK: Record<SignificanceTest, number> = {
  Normal: 0,
  Caution: 1,
  Dangerous: 2,
  'ALERT!': 3,
};

/** Two-sided absolute-z severity used by the tiers (matches the engine's
 *  getSignificanceTest). Sign carries direction; magnitude carries severity. */
export const twoSidedSignificance = (z: number | null | undefined): SignificanceTest | null => {
  if (z == null || !Number.isFinite(z)) return null;
  const a = Math.abs(z);
  if (a < 2) return 'Normal';
  if (a <= 3) return 'Caution';
  if (a <= 4) return 'Dangerous';
  return 'ALERT!';
};

/** Highest severity across the ghost level + non-Tier-5 bucket signals.
 *  Mirrors anomalyView.deriveSeverity. Returns null when all Normal/absent. */
export const formatZScore = (z: number | null | undefined): string =>
  z == null || !Number.isFinite(z) ? '—' : `${z > 0 ? '+' : ''}${z.toFixed(2)}σ`;

// ---------------------------------------------------------------------------
// IAT fallback chain (which mean/stdDev fed the ghost z-score)
// ---------------------------------------------------------------------------

export interface EffectiveIATStats {
  source: 'cyclicality-adjusted' | '30-day-window' | '40-txn-historical' | 'none';
  mean: number;
  stdDev: number;
  label: string;
}

export const getEffectiveIATStats = (
  ghost: GhostTransactionAnalysis | undefined | null,
  meta: MetaAnalysisLite | undefined | null,
): EffectiveIATStats => {
  const cyc = ghost?.cyclicalityAdjustedStatistics;
  const raw = ghost?.interArrivalTimeStatistics;

  if (cyc && cyc.cyclicalityAdjustedStdDevSeconds > 0) {
    return {
      source: 'cyclicality-adjusted',
      mean: cyc.cyclicalityAdjustedMeanSeconds,
      stdDev: cyc.cyclicalityAdjustedStdDevSeconds,
      label: `Cyclicality-adjusted (${cyc.cyclicalityAdjustment})`,
    };
  }
  if (raw && (raw.standardDeviationSeconds ?? 0) > 0) {
    return {
      source: '30-day-window',
      mean: raw.normalizedMeanSeconds ?? 0,
      stdDev: raw.standardDeviationSeconds ?? 0,
      label: '30-day window',
    };
  }
  const m = meta?.interArrivalTimeDistributionNormalizedMeanLast40Transactions ?? 0;
  const sd = meta?.interArrivalTimeDistributionStdDevNormalizedLast40Transactions ?? 0;
  if (sd > 0) {
    return {
      source: '40-txn-historical',
      mean: m,
      stdDev: sd,
      label: 'Historical (last 40 transactions)',
    };
  }
  return { source: 'none', mean: 0, stdDev: 0, label: 'Insufficient data' };
};

/** The gap (seconds) the ghost z-score is actually measured from — the most
 *  delay-adjusted value available, falling back gracefully. */
export const effectiveElapsedSeconds = (ghost: GhostTransactionAnalysis): number =>
  ghost.secondsSinceLastTransactionAdjustedForDataAndAnalysisDelay ??
  ghost.secondsSinceLastTransactionAdjustedForDataDelay ??
  ghost.secondsSinceLastTransaction ??
  0;
